import type { DrinkingWindowYears, WineListRating, WinePlacement } from "@corklog/shared";
import { useTranslation } from "react-i18next";

import { countryName } from "../countries.js";
import { formatScore } from "../score.js";
import { DrinkingWindow } from "./DrinkingWindow.js";
import { Flag } from "./Flag.js";

/**
 * One wine in a list. Rows separated by hairlines, never cards: a bottle lies
 * on a shelf, a card floats.
 *
 * The fixed left gutter carries the wine number, and it is the structural
 * device the whole layout leans on. It encodes something true — that number is
 * physically on the bottle in front of you, so the eye scanning the screen and
 * the hand scanning the shelf are doing the same job in the same order.
 */
export type WineRowData = {
  readonly number: number;
  readonly name: string;
  readonly producer: string;
  /**
   * The region, where the cellar knows one. It reads beside the producer
   * rather than opposite it, because the two are one fact — who made it, and
   * where — and because the slot opposite now carries the country.
   */
  readonly region: string | null;
  /** Two letters. The flag is drawn from it; the name is translated from it. */
  readonly countryCode: string;
  /** Null for a non-vintage wine, which is a third of this cellar. */
  readonly vintage: number | null;
  readonly window: DrinkingWindowYears;

  /** Where its bottles are standing, and how many are out. */
  readonly standing: readonly WinePlacement[];
  readonly away: number;

  /**
   * What anybody thinks of it — the latest reading from each source, the
   * cellar's own first, already ordered by the server. Empty for a wine
   * nobody has scored, and then the row simply has no such line.
   */
  readonly ratings: readonly WineListRating[];

  /**
   * Catalogued since this reader last opened the list.
   *
   * Optional, and absent everywhere but the wine list: *Att öppna*, the meal
   * suggestion, *Luckor* and the walk all draw this row, and none of them is
   * answering *what is new here*. A mark on those screens would be a mark
   * with no line above it explaining itself.
   *
   * The server decides it — see `isNew` in the wine contract. The row only
   * draws it.
   */
  readonly isNew?: boolean;
};

/**
 * Where to reach for this wine, in one line.
 *
 * The screen this row belongs to exists to answer *where is that bottle* in
 * front of a cabinet, and until this line it could only answer it one wine at
 * a time, by being left. Two cabinets read as two clauses rather than as a
 * count with a footnote, because standing at the cabinet the useful fact is
 * which door to open.
 *
 * A wine drunk dry says so instead of showing a blank, which is what the
 * spreadsheet's rows at zero always did: the row stays, because the number on
 * it is on labels, and because buying the wine again attaches to it.
 */
function whereItIs(
  t: ReturnType<typeof useTranslation>["t"],
  standing: readonly WinePlacement[],
  away: number,
): string {
  const places = standing.map((place) =>
    place.storageName === null || place.shelfName === null
      ? // Owned, in the cellar, and on no shelf — an undo whose shelf was
        // removed. Said out loud rather than dropped, because a bottle that
        // physically exists must not be invisible in the view meant to find it.
        t("wines.unplaced")
      : `${place.storageName} · ${place.shelfName}`,
  );
  if (away > 0) places.push(t("wines.away", { count: away }));
  return places.length === 0 ? t("wines.noneLeft") : places.join(", ");
}

export function WineRow({
  wine,
  currentYear,
  caveat,
  note,
}: {
  wine: WineRowData;
  currentYear: number;
  /**
   * What is worth doubting about the band below, in one sentence.
   *
   * Absent on the wine list, and deliberately: a line under every band there
   * would be a column of the word *importen* down a page whose one loud
   * element is the band, which is the same argument `DrinkingWindow` makes for
   * leaving provenance off the row. On *Att öppna* it is the point — a year
   * that is being acted on tonight is worth exactly what it rests on.
   */
  caveat?: string;
  /**
   * Why this row is here, in the words of whatever put it here.
   *
   * Distinct from `caveat`, which doubts the band above it in one line. This
   * is an argument for the row and it runs to two or three sentences — the
   * meal suggestion at `10.1` is the only thing that passes one.
   *
   * It is a prop rather than something the caller renders beside the row
   * because **`WineRow` is the `<li>`**: a caller that wrapped it in one of
   * its own to get somewhere to put the sentence produced `<li>` inside
   * `<li>`, which is what `10.1` did and what CI caught on the commit after
   * it.
   */
  note?: string;
}) {
  const { t } = useTranslation();
  const inCellar = wine.standing.reduce((total, place) => total + place.count, 0);

  return (
    <li className="border-line hover:bg-raised border-b transition-colors">
      <article className="grid grid-cols-[2.75rem_1fr] gap-x-4 py-5 pr-1 pl-1">
        {/* The gutter carries the number, and under it the one thing that is
            true of the row rather than of the wine.

            Here and not beside the name: design-direction.md forbids accenting
            a single word inside a heading, and the name is one. The gutter is
            also the column the eye already runs down, so a mark in it is found
            by the same scan that finds the number — and it is a word rather
            than a colour, which is the same rule the window band keeps. */}
        <div className="pt-0.5 text-right">
          <div className="font-display text-muted text-lg">{wine.number}</div>
          {wine.isNew === true && (
            <div className="text-accent mt-0.5 text-xs">{t("wines.new")}</div>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-display truncate text-lg leading-tight">{wine.name}</h3>
            <span className="font-display text-muted shrink-0 text-base">
              {wine.vintage ?? "NV"}
            </span>
          </div>

          <div className="text-muted mt-0.5 flex items-baseline justify-between gap-4 text-sm">
            <span className="truncate">{wine.producer}</span>
            {/* Place, behind its flag: Piemonte, Italien. Narrowest first, the
                way an address is written and the way the eye wants it — the
                region is the specific fact and the country is the one it sits
                in. Joined with a comma rather than a middle dot, which
                design-direction.md lists among the things this interface is
                not.

                Two spans rather than one string, so the truncation falls on
                the region: a long region losing its tail still reads, and the
                country stays whole. A wine whose country is known but whose
                region is not is common; the reverse never happens. */}
            <span className="flex min-w-0 shrink items-baseline gap-1.5">
              <Flag countryCode={wine.countryCode} />
              {wine.region !== null && <span className="truncate">{wine.region},</span>}
              <span className="shrink-0">{countryName(wine.countryCode)}</span>
            </span>
          </div>

          {/* Judgement sits with the label facts above it and not with the
              bottle facts below, because what somebody thinks of a wine is
              true of every bottle of it. The source and the scale are printed
              with the score and not hidden behind it: a bare 4,1 is unreadable
              and an unsigned 4,1 is a claim nobody made. Absent where nobody
              has scored the wine — a line saying so would be a line on every
              row saying nothing. */}
          {wine.ratings.length > 0 && (
            <div className="text-muted mt-0.5 flex flex-wrap items-baseline gap-x-4 text-sm">
              {wine.ratings.map((rating) => (
                <span key={rating.sourceName}>
                  {rating.sourceName}{" "}
                  <span className="font-display">
                    {formatScore(rating.score, rating.scaleHasDecimals)}
                  </span>
                  {" / "}
                  {rating.scaleMax}
                </span>
              ))}
            </div>
          )}

          {/* Under the producer and above the window: where it is, then when
              it is ready. Both are things about the bottle rather than about
              the label, and where comes first because that is the question
              asked standing in front of the cabinet. */}
          <div className="text-muted mt-0.5 flex items-baseline justify-between gap-4 text-sm">
            <span className="truncate">{whereItIs(t, wine.standing, wine.away)}</span>
            {inCellar > 0 && <span className="font-display shrink-0">{inCellar}</span>}
          </div>

          <DrinkingWindow years={wine.window} currentYear={currentYear} yearsOnBand />
          {caveat !== undefined && <p className="text-muted mt-1 text-xs">{caveat}</p>}
          {note !== undefined && (
            <p className="text-muted mt-2 max-w-prose text-sm">{note}</p>
          )}
        </div>
      </article>
    </li>
  );
}
